import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';
import userRoleService, { ActivatedRolesResponse, RoleProfileDTO } from '../services/userRoleService';
import { userEducationService, EducationEntry } from '../services/userEducationService';
import { userLanguageService } from '../services/userLanguageService';
import EducationSection from '../components/profile/EducationSection';
import LanguagesSection from '../components/profile/LanguagesSection';
import TutorRoleSection from '../components/profile/TutorRoleSection';
import TeacherRoleSection from '../components/profile/TeacherRoleSection';

interface PageState {
  rolesResponse: ActivatedRolesResponse | null;
  educationEntries: EducationEntry[];
  languages: string[];
  loading: boolean;
  error: string | null;
}

const UserProfileSettingsPage: React.FC = () => {
  const [state, setState] = useState<PageState>({
    rolesResponse: null,
    educationEntries: [],
    languages: [],
    loading: true,
    error: null,
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [rolesResponse, educationEntries, languages] = await Promise.all([
          userRoleService.getActivatedRoles(),
          userEducationService.getEducationEntries(),
          userLanguageService.getLanguages(),
        ]);
        setState({ rolesResponse, educationEntries, languages, loading: false, error: null });
      } catch (err: any) {
        setState(s => ({ ...s, loading: false, error: err.message || 'Failed to load profile data' }));
      }
    };
    load();
  }, []);

  const activatedRoles: RoleProfileDTO[] = state.rolesResponse?.activatedRoles ?? [];
  const tutorProfile = activatedRoles.find(r => r.roleType === 'TUTOR');
  const teacherProfile = activatedRoles.find(r => r.roleType === 'TEACHER');

  const handleRoleRemoved = (roleType: string) => {
    setState(s => ({
      ...s,
      rolesResponse: s.rolesResponse
        ? { ...s.rolesResponse, activatedRoles: s.rolesResponse.activatedRoles.filter(r => r.roleType !== roleType) }
        : s.rolesResponse,
    }));
  };

  if (state.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
          <AccountCircle sx={{ fontSize: 36, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight={600}>Profile Settings</Typography>
        </Box>

        {state.error && <Alert severity="error" sx={{ mb: 3 }}>{state.error}</Alert>}

        {/* Account Info */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Account</Typography>
            <Typography variant="body2" color="text.secondary">
              Name and email are managed through your account settings.
            </Typography>
          </CardContent>
        </Card>

        {/* Education */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <EducationSection
              entries={state.educationEntries}
              onEntriesChange={entries => setState(s => ({ ...s, educationEntries: entries }))}
            />
          </CardContent>
        </Card>

        {/* Languages */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <LanguagesSection
              languages={state.languages}
              onLanguagesChange={languages => setState(s => ({ ...s, languages }))}
            />
          </CardContent>
        </Card>

        {/* Tutor Role Section — shown only when TUTOR is active */}
        {tutorProfile && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <TutorRoleSection
                  profession={tutorProfile.profession ?? ''}
                  activatedRolesCount={activatedRoles.length}
                  onRemoved={() => handleRoleRemoved('TUTOR')}
                />
              </CardContent>
            </Card>
          </>
        )}

        {teacherProfile && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <TeacherRoleSection
                  profession={teacherProfile.profession ?? ''}
                  institutionName={teacherProfile.institutionName}
                  institutionCity={teacherProfile.institutionCity}
                  institutionCountry={teacherProfile.institutionCountry}
                  institutionType={teacherProfile.institutionType}
                  subjectsTaught={teacherProfile.subjectsTaught}
                  activatedRolesCount={activatedRoles.length}
                  onRemoved={() => handleRoleRemoved('TEACHER')}
                />
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </Box>
  );
};

export default UserProfileSettingsPage;
